import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import {
  FaFilePdf,
  FaEnvelope,
  FaWhatsapp,
  FaSyncAlt,
  FaCheckCircle,
  FaStore,
  FaReceipt,
  FaEye,
  FaRss
} from 'react-icons/fa';
import './NfcInvoicePortal.css';

const NfcInvoicePortal = () => {
  const { locationId } = useParams();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [newBillFlash, setNewBillFlash] = useState(false);

  const fetchLatestBill = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/BillsValises/nfc/latest/${locationId}`);
      if (res.data && res.data.bill) {
        setBill(res.data.bill);
      } else {
        setError('Aucune facture trouvée pour ce point de vente.');
      }
    } catch (err) {
      console.error('Erreur NFC bill fetch:', err);
      setError(err.response?.data?.message || 'Facture introuvable ou caisse en attente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestBill();

    // Connexion Socket.IO pour recevoir la facture en direct dès que le caissier valide le paiement
    const socketUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? `${window.location.protocol}//${window.location.hostname}:5003`
      : window.location.origin;

    const socket = io(socketUrl, {
      transports: ['websocket', 'polling']
    });
    
    socket.on('connect', () => {
      console.log('📡 Connecté au serveur Socket.IO NFC sur', socketUrl);
      socket.emit('join_caisse', locationId);
    });

    socket.on('nfc_bill_updated', (newBillData) => {
      console.log('⚡ Nouvelle facture reçue en temps réel:', newBillData);
      setBill(newBillData);
      setLoading(false);
      setError(null);
      setNewBillFlash(true);
      setTimeout(() => setNewBillFlash(false), 3000);
    });

    return () => {
      socket.disconnect();
    };
  }, [locationId]);

  const getPdfUrl = () => {
    if (!bill?._id) return '#';
    const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? `${window.location.protocol}//${window.location.hostname}:5003`
      : window.location.origin;
    return `${baseUrl}/api/BillsValises/nfc/pdf/${bill._id}`;
  };

  const handleDownloadPdf = () => {
    if (!bill?._id) return;
    window.open(getPdfUrl(), '_blank');
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      alert('Veuillez saisir une adresse email valide.');
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post('/api/BillsValises/sendReceiptEmail', {
        clientEmail: email,
        billNumber: bill.billNumber,
        total: bill.total,
        items: bill.items,
        locationName: bill.localisation?.name || 'Point de vente',
        locationId: bill.localisation?._id || locationId,
        cashierName: bill.user?.name || 'Caissier',
        paymentMethod: bill.paymentMethod
      });
      setEmailSuccess(true);
      setTimeout(() => {
        setShowEmailInput(false);
        setEmailSuccess(false);
      }, 3000);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'envoi de l'email.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleShareWhatsapp = () => {
    if (!bill) return;

    const itemsSummary = (bill.items || [])
      .map((item) => `  • *${item.name}* x${item.quantity || 1} ──> *${((item.price || 0) * (item.quantity || 1)).toFixed(2)} DT*`)
      .join("\n");

    const text = `🧾 *REÇU DE CAISSE OFFICIEL*
📍 *${bill.localisation?.name || 'Point de vente'}*

━━━━━━━━━━━━━━━━━━━━━
🆔 *Ticket N° :* #${bill.billNumber}
📅 *Date :* ${new Date(bill.date || bill.createdAt).toLocaleDateString('fr-FR')} ${new Date(bill.date || bill.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
👤 *Caissier :* ${bill.user?.name || 'Caisse'}
💳 *Paiement :* ${bill.paymentMethod || 'Espèce'}
━━━━━━━━━━━━━━━━━━━━━

🛍️ *DÉTAIL DES ACHATS :*
${itemsSummary}

━━━━━━━━━━━━━━━━━━━━━
💰 *TOTAL RÉGLÉ : ${(bill.total || 0).toFixed(2)} DT*
━━━━━━━━━━━━━━━━━━━━━

📄 *Télécharger votre Facture PDF officielle :*
${getPdfUrl()}

✨ _Merci de votre visite et à très bientôt !_`;

    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
  };

  const locationName = bill?.localisation?.name || 'Point de Vente';

  return (
    <div className="nfc-portal-container">
      <div className={`nfc-portal-card ${newBillFlash ? 'border-emerald-500 shadow-emerald-500/50' : ''}`}>
        
        {/* En-tête de la caisse */}
        <div className="nfc-header">
          <div className="nfc-live-badge">
            <span className="nfc-pulse-dot"></span>
            <FaRss className="inline" /> Caisse NFC & QR Code en direct
          </div>
          <h1 className="nfc-location-title">{locationName}</h1>
          <p className="nfc-subtitle">Scannage QR Code ou NFC - Facture électronique officielle</p>
        </div>

        <div className="nfc-body">
          {loading ? (
            <div className="nfc-empty-state">
              <FaSyncAlt className="nfc-empty-icon animate-spin" />
              <h3>Recherche de votre facture...</h3>
              <p className="text-slate-400 text-sm">Veuillez patienter pendant la récupération en direct.</p>
            </div>
          ) : error && !bill ? (
            <div className="nfc-empty-state">
              <FaReceipt className="nfc-empty-icon text-amber-400" />
              <h3>En attente de la vente</h3>
              <p className="text-slate-400 text-sm">{error}</p>
              <button onClick={fetchLatestBill} className="nfc-btn-secondary mt-4 w-full justify-center">
                <FaSyncAlt /> Rafraîchir
              </button>
            </div>
          ) : (
            <>
              {/* Détails Métadonnées Vente */}
              <div className="nfc-meta-grid">
                <div className="nfc-meta-item">
                  <span className="nfc-meta-label">Ticket N°</span>
                  <span className="nfc-meta-value">#{bill.billNumber}</span>
                </div>
                <div className="nfc-meta-item">
                  <span className="nfc-meta-label">Paiement</span>
                  <span className="nfc-meta-value">{bill.paymentMethod || 'Espèce'}</span>
                </div>
                <div className="nfc-meta-item">
                  <span className="nfc-meta-label">Date & Heure</span>
                  <span className="nfc-meta-value">
                    {new Date(bill.date || bill.createdAt).toLocaleDateString('fr-FR')} {new Date(bill.date || bill.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="nfc-meta-item">
                  <span className="nfc-meta-label">Caissier</span>
                  <span className="nfc-meta-value">{bill.user?.name || 'Caisse 1'}</span>
                </div>
              </div>

              {/* Articles de la facture */}
              <div className="nfc-items-section">
                <div className="nfc-items-header">Détail des achats ({bill.items?.length || 0})</div>
                <div className="nfc-items-list">
                  {bill.items?.map((item, idx) => (
                    <div key={idx} className="nfc-item-row">
                      <div>
                        <span className="nfc-item-name">{item.name}</span>
                        <span className="nfc-item-qty">x{item.quantity || 1}</span>
                      </div>
                      <span className="nfc-item-price">
                        {((item.price || 0) * (item.quantity || 1)).toFixed(2)} DT
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Récapitulatif Totaux */}
              <div className="nfc-summary-box">
                {bill.discount > 0 && (
                  <div className="nfc-summary-row">
                    <span>Remise accordée :</span>
                    <span>-{bill.discount.toFixed(2)} DT</span>
                  </div>
                )}
                {bill.tva > 0 && (
                  <div className="nfc-summary-row">
                    <span>dont TVA :</span>
                    <span>{bill.tva.toFixed(2)} DT</span>
                  </div>
                )}
                <div className="nfc-summary-total">
                  <span>TOTAL REGLÉ</span>
                  <span>{bill.total?.toFixed(2)} DT</span>
                </div>
              </div>

              {/* Actions Téléchargement / Partage */}
              <div className="nfc-actions-grid">
                <button onClick={handleDownloadPdf} className="nfc-btn-primary">
                  <FaFilePdf size={18} /> Télécharger la Facture PDF
                </button>

                <button onClick={handleDownloadPdf} className="nfc-btn-secondary">
                  <FaEye /> Aperçu PDF
                </button>

                <button onClick={() => setShowEmailInput(!showEmailInput)} className="nfc-btn-secondary">
                  <FaEnvelope /> Recevoir par Email
                </button>

                <button onClick={handleShareWhatsapp} className="nfc-btn-secondary">
                  <FaWhatsapp className="text-green-400" /> Partager WhatsApp
                </button>
              </div>

              {/* Formulaire d'envoi par Email */}
              {showEmailInput && (
                <div className="nfc-email-box">
                  <span className="text-xs font-semibold text-slate-300">Recevoir le ticket PDF sur votre boite mail:</span>
                  {emailSuccess ? (
                    <div className="flex items-center gap-2 text-emerald-400 text-sm mt-2 font-medium">
                      <FaCheckCircle /> Reçu envoyé avec succès à {email} !
                    </div>
                  ) : (
                    <form onSubmit={handleSendEmail} className="nfc-email-input-group">
                      <input
                        type="email"
                        placeholder="Ex: client@gmail.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="nfc-email-input"
                        required
                      />
                      <button type="submit" disabled={sendingEmail} className="nfc-email-send-btn">
                        {sendingEmail ? '...' : 'Envoyer'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="nfc-footer">
          TASKO POS © {new Date().getFullYear()} • Ticket de caisse dématérialisé NFC
        </div>
      </div>
    </div>
  );
};

export default NfcInvoicePortal;
